import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutGrid, Sparkles, Users, LogOut, Zap, Images, MessageSquare, Settings as SettingsIcon, ShieldCheck, ShoppingBag, Brain } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";

const NavBtn = ({ to, active, icon: Icon, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
      active ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:bg-[var(--surface-2)]"
    }`}
  >
    <Icon className="h-4 w-4" strokeWidth={1.5} />
    {children}
  </Link>
);

const BadgeNav = ({ to, active, icon: Icon, children, testid, count }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
      active ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)] hover:bg-[var(--surface-2)]"
    }`}
  >
    <Icon className="h-4 w-4" strokeWidth={1.5} />
    {children}
    {count > 0 && (
      <span data-testid={`${testid}-badge`} className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--brand)] px-1 text-[10px] font-bold text-white">
        {count}
      </span>
    )}
  </Link>
);

const TabLink = ({ to, active, icon: Icon, children, testid }) => (
  <Link to={to} data-testid={testid}
    className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${active ? "bg-white text-[var(--ink)] shadow-sm" : "text-[var(--ink-soft)]"}`}>
    <Icon className="h-4 w-4" strokeWidth={1.75} />{children}
  </Link>
);

const MenuItem = ({ onClick, icon: Icon, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-[var(--ink)] transition-colors hover:bg-[var(--surface-2)]">
    <Icon className="h-4 w-4 text-[var(--ink-soft)]" strokeWidth={1.5} />{children}
  </button>
);

export const Header = () => {
  const { pathname } = useLocation();
  const { status, user, company, logout } = useAuth();
  const navigate = useNavigate();
  const logo = company?.logo_url ? mediaUrl(company.logo_url) : "/customize-logo.jpg";
  const isEditor = user && ["owner", "manager"].includes(user.role);
  const isRep = user && user.role === "sales_rep";
  const isSuper = user?.is_superadmin;
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (status !== "authed") return;
    let live = true;
    const poll = async () => {
      try {
        const { data } = await api.get(isSuper ? "/admin/unread-total" : "/messages/unread-count");
        if (live) setUnread(data.unread || 0);
      } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("unread poll failed", e); }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { live = false; clearInterval(t); };
  }, [status, isSuper, pathname]);

  return (
    <header
      data-testid="app-header"
      className={`sticky top-0 z-40 flex items-center justify-between border-b border-[var(--line)] bg-white/80 px-6 py-3 backdrop-blur-xl md:px-10 ${status === "authed" ? "lg:hidden" : ""}`}
    >
      <Link to={status === "authed" ? "/studio" : "/"} data-testid="brand-link" className="flex items-center gap-3">
        <img src={logo} alt="Customize Studio" className={status === "authed"
          ? "h-9 w-auto max-w-[150px] object-contain md:h-10 md:max-w-[190px]"
          : "h-16 w-auto max-w-[200px] object-contain md:h-28 md:max-w-[420px]"} />
      </Link>

      {status === "authed" ? (
        <nav className="flex items-center gap-2">
          {isRep ? (
            <div className="flex items-center gap-1 rounded-full bg-[var(--surface-2)] p-1" data-testid="rep-tabs">
              <TabLink to="/studio" active={pathname === "/studio"} icon={Sparkles} testid="rep-tab-studio">Studio</TabLink>
              <TabLink to="/history" active={pathname === "/history"} icon={Images} testid="rep-tab-history">History</TabLink>
            </div>
          ) : (
            <div className="hidden items-center gap-2 md:flex">
              <NavBtn to="/studio" active={pathname === "/studio"} icon={Sparkles} testid="nav-studio">Studio</NavBtn>
              <NavBtn to="/history" active={pathname === "/history"} icon={Images} testid="nav-history">History</NavBtn>
              {isEditor && <NavBtn to="/library" active={pathname === "/library"} icon={LayoutGrid} testid="nav-library">Library</NavBtn>}
              {isEditor && <NavBtn to="/team" active={pathname === "/team"} icon={Users} testid="nav-team">Team</NavBtn>}
              <NavBtn to="/store" active={pathname === "/store"} icon={ShoppingBag} testid="nav-store">Store</NavBtn>
              {!isSuper && (
                <BadgeNav to="/messages" active={pathname === "/messages"} icon={MessageSquare} count={unread} testid="nav-messages">Messages</BadgeNav>
              )}
              {isSuper && (
                <>
                  <NavBtn to="/brain" active={pathname === "/brain"} icon={Brain} testid="nav-brain">Brain</NavBtn>
                  <BadgeNav to="/console" active={pathname === "/console"} icon={ShieldCheck} count={unread} testid="nav-console">Admin</BadgeNav>
                </>
              )}
            </div>
          )}
          <div className="relative">
            <button data-testid="gear-menu-btn" onClick={() => setMenuOpen((v) => !v)} title="Menu"
              className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--ink-soft)] transition-colors hover:bg-[var(--surface-2)]">
              <SettingsIcon className="h-5 w-5" strokeWidth={1.5} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div data-testid="gear-menu" className="absolute right-0 top-11 z-50 w-56 rounded-xl border border-[var(--line)] bg-white p-1.5 shadow-[0_10px_40px_rgba(0,0,0,0.12)]">
                  <p className="truncate px-3 py-2 text-xs text-[var(--ink-soft)]">{user.name || user.email}</p>
                  <div className="my-1 h-px bg-[var(--line)]" />
                  {user.role === "owner" && <MenuItem onClick={() => { navigate("/settings"); setMenuOpen(false); }} icon={SettingsIcon} testid="menu-settings">Settings</MenuItem>}
                  {isEditor && <MenuItem onClick={() => { navigate("/team"); setMenuOpen(false); }} icon={Users} testid="menu-team">Team</MenuItem>}
                  <MenuItem onClick={() => { logout(); navigate("/"); }} icon={LogOut} testid="menu-logout">Log out</MenuItem>
                </div>
              </>
            )}
          </div>
        </nav>
      ) : (
        <nav className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-login" className="rounded-full px-4 py-2 text-sm font-medium text-[var(--ink-soft)] hover:bg-[var(--surface-2)]">Log in</Link>
          <Link to="/signup" data-testid="nav-signup" className="rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)]">Sign up</Link>
        </nav>
      )}
    </header>
  );
};
